import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { UserModel } from '../../UsersTable';
import Fetcher, { URLBuild } from '../../../services/fetcher';
import settings from '../../../settings';
import { ColDef, XGrid, useApiRef, PageChangeParams, ValueFormatterParams } from '@material-ui/x-grid';
import { FiltersInterface } from '../consts';
import UserModal from '../../UserModal';
import { useNotification } from '../../../contexts/Notification';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: 15
  }
}));

function dateFormatter(params: ValueFormatterParams): string {
  const date: Date = params.value as Date;
  if (date === null)
    return '';
  return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function displayBooleanInFrench(params: ValueFormatterParams): string {
  if (params.value === null || params.value === undefined)
    return '';
  else if (params.value as boolean)
    return 'Oui';
  else if (!(params.value as boolean))
    return 'Non';
  return '';
}

function displayPeriodInFrench(params: ValueFormatterParams): string {
  if (params.value === null || params.value === undefined)
    return '';
  else if (params.value as string === 'month')
    return 'Mensuelle';
  else if (params.value as string === 'year')
    return 'Annuelle';
  return '';
}

const columns: ColDef[] = [
  {field: 'id', headerName: 'ID', width: 100, sortable: false},
  {field: 'active', headerName: 'Active', width: 100, sortable: false, valueFormatter: displayBooleanInFrench},
  {field: 'paymentWay', headerName: 'Service', width: 150, sortable: false},
  {field: 'period', headerName: 'Récurrence', width: 150, sortable: false, valueFormatter: displayPeriodInFrench},
  {field: 'role', headerName: 'Abonnement', width: 150, sortable: false},
  {field: 'cancelAtEnd', headerName: 'Renouvellement auto', width: 250, sortable: false, valueFormatter: displayBooleanInFrench},
  {field: 'startDate', headerName: 'Date de début', width: 230, sortable: false, valueFormatter: dateFormatter},
  {field: 'endDate', headerName: 'Date de fin', width: 230, sortable: false, valueFormatter: dateFormatter},
];

interface RowDef {
  id: string,
  active: boolean,
  paymentWay: string,
  period: string
  role: string,
  startDate: Date,
  endDate: Date,
  cancelAtEnd: boolean,
};

interface Props {
  filters: FiltersInterface
}

const AllSubscriptionGrid = (props: Props) => {
  const apiRef = useApiRef();
  const classes = useStyles();
  const limit = 15;
  const notification = useNotification();
  const [rows, setRows] = useState<RowDef[]>([]);
  const [nbRows, setNbRows] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<any>({});
  const [lastPages, setLastPages] = useState<any[]>([]);
  const [userData, setUserData] = useState<UserModel | undefined>(undefined);

  useEffect(() => {
    if (Object.keys(props.filters).length === 0 && Object.keys(params).length === 0)
      return;
    setParams(props.filters);
  }, [props.filters]);
  
  useEffect(() => {
    getData();
  }, [params]);
  
  const getData = () => {
    setLoading(true);
    Fetcher.get(
      URLBuild(settings.PaymentProvider.paymentApi + '/user-sub/list', {limit, ...params})
    ).then(data => {
      setNbRows(data.totalCount);
      setRows(data.data.map((row: any) => {
        return {
          id: row.userId,
          startDate: new Date(row.startDate * 1000),
          endDate: row.endDate ? new Date(row.endDate * 1000) : null,
          active: row.active,
          paymentWay: row.paymentWay,
          period: row.period,
          role: row.role,
          cancelAtEnd: !row.cancelAtEnd
        };
      }));
      setLoading(false);
    }).catch((e: Error) => {
      notification.notify(e.message, "error");
      console.error(e);
      setLoading(false);
    });
  };

  const onPageChange = (param: PageChangeParams) => {
    if (param.rowCount === 0)
      return
    if (param.page === 1) {
      setParams((oldParam: any) => {
        const newParams = {...oldParam};
        if (newParams.lastUserId)
          delete newParams.lastUserId;
        return newParams;
      });
      setLastPages([]);
    } else {
      let exclusiveStart = {};
      if (lastPages.length === param.page - 2) {
        exclusiveStart = {lastUserId: rows[rows.length - 1].id};
        const newLastPages = [...lastPages];
        newLastPages.push(exclusiveStart);
        setLastPages([...newLastPages]);
      } else {
        const newLastPages = [...lastPages];
        newLastPages.pop();
        exclusiveStart = newLastPages[newLastPages.length - 1];
        setLastPages(newLastPages);
      }
      setParams((oldParam: any) => ({...oldParam, ...exclusiveStart}));
    }
  };

  const selectUser = (i: number) => {
    Fetcher.get(URLBuild(settings.OpenIdProvider.users + '/' + rows[i].id, {}))
      .then((res) => {
        setUserData(res);
      }).catch((e) => {
        notification.notify(`Impossible de récupérer l'utilisateur ${rows[i].id}`, "error");
        console.error(e)
      });
  }

  return (
    <div className={classes.container}>
      <XGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        pageSize={limit}
        paginationMode="server"
        pagination={true}
        loading={loading}
        hideFooterPagination={loading}
        rowCount={nbRows}
        hideFooterRowCount={true}
        onPageChange={onPageChange}
        onRowClick={(param) => selectUser(param.rowIndex)}
      />
      { !!userData &&
        <UserModal
          open={!!userData}
          onClose={() => setUserData(undefined)}
          user={userData as UserModel}
          updatable={false}
          onRequestClose={() => setUserData(undefined)}
        />
      }
    </div>
  );
}

export default AllSubscriptionGrid;